import { computed, createApp, defineComponent, onMounted, ref } from "vue";
import LoginPage from './LoginPage';
import InputText from "../forms/InputText";
import Utils, { UserError } from "../utils/Utils";
import * as moment from "moment";

interface LoginInfo {
    id: number;
    number: string;
    time: Date;
    next: Date;
    step: number;
}

export default defineComponent({
    components: {
        InputText,
    },

    template: `
        <div v-if="info" class="LoginPage">
            <div class="LoginPage__wrapper wrapper wrapper--500">
                <template v-if="step == 1">
                    <h1 class="title-2">Wysłaliśmy Ci SMS z kodem dostępu</h1>

                    <p>By mieć pewność, że Twoje dane są bezpieczne, prosimy o <b>wpisanie 6-cyfrowego kodu</b>, który wysłaliśmy na numer {{ info.number }} o {{ code_time_format }}.</p>

                    <InputText v-model="code" mask="000000" :error="code_error" />

                    <button type="button" class="button button--primary" :disabled="loading || !valid" @click="onSubmitCode">Zaloguj się</button>
                    <button type="button" class="button button--secondary" :disabled="loading || resend_disabled" @click="onSubmitResend">Wyślij kod ponownie</button>
                </template>

                <template v-if="step == 2">
                    <h1 class="title-2">Wysłaliśmy Ci nowy kod</h1>

                    <p>By mieć pewność, że Twoje dane są bezpieczne, prosimy o wpisanie 6-cyfrowego kodu, który wysłaliśmy o {{ code_time_format }}. Kod jest ważny przez 5 minut.</p>

                    <InputText v-model="code" mask="000000" :error="code_error" />

                    <button type="button" class="button button--primary" :disabled="loading || !valid" @click="onSubmitCode">Zaloguj się</button>
                    <button type="button" class="button button--secondary" :disabled="loading || resend_disabled" @click="onSubmitResend">Wyślij kod ponownie</button>

                    <div v-if="resend_timer" class="LoginPage__timer">
                        Następny kod będziemy mogli wysłać za:<br><b>{{ resend_timer }}</b>
                    </div>
                </template>

                <template v-if="step == 50">
                    <svg class="LoginPage__icon" xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none">
                        <path d="M38.7199 22.24C38.2799 21.48 37.2999 21.2 36.5399 21.66C35.7799 22.1 35.5199 23.08 35.9599 23.84L43.2799 36.54C43.7599 37.36 43.7599 38.34 43.2799 39.14C42.7999 39.96 41.9599 40.44 41.0199 40.44H6.97991C6.03991 40.44 5.19991 39.96 4.71991 39.14C4.23991 38.32 4.23991 37.34 4.71991 36.54L21.7399 7.04004C22.6799 5.40004 25.3199 5.40004 26.2599 7.04004L32.9199 18.58C33.3599 19.34 34.3399 19.6 35.0999 19.16C35.8599 18.72 36.1199 17.74 35.6799 16.98L29.0199 5.44004C27.9799 3.62004 26.0799 2.54004 23.9799 2.54004C21.8799 2.54004 19.9999 3.62004 18.9399 5.44004L1.93991 34.92C0.899912 36.74 0.899912 38.92 1.93991 40.74C2.99991 42.56 4.87991 43.64 6.97991 43.64H41.0199C43.1199 43.64 44.9999 42.56 46.0599 40.74C47.0999 38.92 47.0999 36.74 46.0599 34.92L38.7399 22.22L38.7199 22.24Z" fill="#005537"/>
                        <path d="M25.5999 27.2001V15.2401C25.5999 14.3601 24.8799 13.6401 23.9999 13.6401C23.1199 13.6401 22.3999 14.3601 22.3999 15.2401V27.2001C22.3999 28.0801 23.1199 28.8001 23.9999 28.8001C24.8799 28.8001 25.5999 28.0801 25.5999 27.2001Z" fill="#005537"/>
                        <path d="M21.6399 34.7599C21.6399 36.0599 22.6999 37.1199 23.9999 37.1199C25.2999 37.1199 26.3599 36.0599 26.3599 34.7599C26.3599 33.4599 25.2999 32.3999 23.9999 32.3999C22.6999 32.3999 21.6399 33.4599 21.6399 34.7599Z" fill="#005537"/>
                    </svg>

                    <h1 class="title-2">Logowanie nie powiodło się</h1>

                    <p>System jest tymczasowo niedostępny. Spróbuj ponownie za kilka minut.</p>

                    <button type="button" class="button button--primary" :disabled="loading" @click="onReset">Spróbuj ponownie</button>
                </template>
            </div>
        </div>
    `,

    setup(props, ctx) {
        const loading = ref(false);
        const step = ref(1);
        const info = ref(null as LoginInfo);
        const code = ref(null as string);
        const code_error = ref(null as string);
        const resend_disabled = ref(false);
        const resend_timer = ref(null as string);

        const code_time_format = computed(() => {
            return info.value.time ? moment(info.value.time).format('HH:mm') : null;
        });

        const valid = computed(() => {
            return code.value?.match(/^\d{6}$/);
        });

        async function onSubmitCode() {
            loading.value = true;

            try {
                code_error.value = null;
                resend_disabled.value = false

                const r = await Utils.setCart('login.code', {
                    code: code.value,
                }, false);

                window.location.reload();
            } catch (e) {
                if (e instanceof UserError) {
                    code_error.value = e.message;
                } else {
                    step.value = 50;
                }
            } finally {
                loading.value = false;
            }
        }

        async function onSubmitResend() {
            loading.value = true;

            try {
                code.value = null;
                code_error.value = null;
                resend_disabled.value = false;

                const r = await Utils.setCart('login.resend', null, false);

                info.value = r.info;
                step.value = 2;
            } catch (e) {
                step.value = 50;
            } finally {
                loading.value = false;
            }
        }

        async function onReset() {
            loading.value = true;

            try {
                step.value = 1;
            } finally {
                loading.value = false;
            }
        }

        async function reload() {
            const r = await Utils.setCart('login.info', null, false);

            info.value = r.info;
        }

        setInterval(() => {
            const cansend = info.value && moment().isAfter(info.value.next);

            resend_disabled.value = !cansend;

            if (cansend) {
                resend_timer.value = null;
            } else {
                const dur = moment.duration(moment(info.value.next).diff(moment()));
                const min = Math.floor(dur.minutes());
                const sec = dur.seconds();
                const text = `${min}:${String(sec).padStart(2, '0')}`;

                resend_timer.value = text;
            }
        }, 500);

        onMounted(() => {
            reload();
        });

        Utils.attachBodyClass('LoginPage');

        return {
            loading,
            step,
            info,
            code,
            code_error,
            resend_disabled,
            resend_timer,

            code_time_format,
            valid,

            onSubmitCode,
            onSubmitResend,
            onReset,
        };
    },
});

export class LoginPageApp {
    public static init($uid: JQuery): void {
        const app = createApp({
            components: {
                LoginPage,
            },

            template: `
                <LoginPage
                />
            `,
        }).mount($uid[0]);
    }
}
